import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import saleService from '../services/saleService';
import platformService from '../services/platformService';
import { productService } from '../services/productService';
import {
  convertUTCToMadridDateTimeLocalString,
  convertMadridDateTimeLocalToUTCString,
  convertMadridDateToUTCString // Para executionDate que es solo fecha
} from '../utils/dateUtils'; // Importar las nuevas utilidades
import { toast } from 'react-hot-toast';
import { countryOptions, getCountryCode } from '../utils/countries';

const SaleFormScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  
  const [platforms, setPlatforms] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  
  // Inicializar saleDate con la hora actual de Madrid
  const initialSaleDate = convertUTCToMadridDateTimeLocalString(new Date().toISOString());

  const [formData, setFormData] = useState({
    saleDate: initialSaleDate,
    executionDate: '',
    platformId: '',
    orderId: '',
    amount: '',
    commission: '',
    notes: '',
    status: 'pending',
    customerId: '',
    productId: null,
    productName: '',
    campaign: '',
    article: '',
    currency: 'EUR',
    customerCountry: '',
    gclid: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError('');

        // Cargar plataformas
        const platformsData = await platformService.getActivePlatforms();

        if (!isMounted) return;

        // Establecer las plataformas
        setPlatforms(platformsData || []);
        
        if (isEditMode) {
          const saleData = await saleService.getSaleById(id);
          
          if (!isMounted) return;

          if (saleData) {
            // Cargar productos específicos de la plataforma
            if (saleData.platformId) {
              const productsData = await productService.getProducts({ 
                platform: saleData.platformId,
                limit: 1000 
              });
              
              if (productsData?.products) {
                setProducts(productsData.products);
                setFilteredProducts(productsData.products);
              }
            }

            // Establecer los datos del formulario
            const formDataToSet = {
              ...saleData,
              saleDate: convertUTCToMadridDateTimeLocalString(saleData.saleDate),
              executionDate: saleData.executionDate ? convertUTCToMadridDateTimeLocalString(saleData.executionDate).slice(0,10) : '',
              platformId: saleData.platformId || '',
              productId: saleData.productId || null,
              productName: saleData.productName || '',
              campaign: saleData.campaign || '',
              article: saleData.article || '',
              customerId: saleData.customerId || '',
              customerCountry: getCountryCode(saleData.customerCountry) || '',
              notes: saleData.notes || '',
              gclid: saleData.gclid || ''
            };
            setFormData(formDataToSet);
          }
        } else {
          // En modo creación, cargar todos los productos
          const productsData = await productService.getProducts({ limit: 1000 });
          if (productsData?.products) {
            setProducts(productsData.products);
          }
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Error al cargar datos:', err);
        setError('Error al cargar los datos. Por favor, intenta nuevamente.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [id, isEditMode]);

  useEffect(() => {
    if (!formData.platformId) {
      setFilteredProducts([]);
      return;
    }

    const filtered = products.filter(product => 
      product.platformId === parseInt(formData.platformId)
    );
    setFilteredProducts(filtered);

    // Solo limpiar el producto si no existe en los productos filtrados y no estamos en modo edición
    if (!isEditMode && formData.productId && !filtered.find(p => p.id === formData.productId)) {
      setFormData(prev => ({
        ...prev,
        productId: null,
        productName: ''
      }));
    }
  }, [formData.platformId, formData.productId, products, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'platformId' && value) {
      const selectedPlatform = platforms.find(p => p.id === parseInt(value));
      
      // Cargar productos específicos de la plataforma
      const loadPlatformProducts = async () => {
        try {
          const productsData = await productService.getProducts({ platform: value });
          
          if (productsData?.products) {
            setProducts(productsData.products);
            setFilteredProducts(productsData.products);
          }
        } catch (err) {
          console.error('Error al cargar productos de la plataforma:', err);
          setError('Error al cargar los productos de la plataforma');
        }
      };
      
      loadPlatformProducts();

      if (selectedPlatform && formData.amount) {
        const commissionAmount = (parseFloat(formData.amount) * selectedPlatform.commission) / 100;
        setFormData(prev => ({
          ...prev,
          commission: commissionAmount.toFixed(2)
        }));
      }
    }

    if (name === 'amount' && value && formData.platformId) {
      const selectedPlatform = platforms.find(p => p.id === parseInt(formData.platformId));
      if (selectedPlatform) {
        const commissionAmount = (parseFloat(value) * selectedPlatform.commission) / 100;
        setFormData(prev => ({
          ...prev,
          commission: commissionAmount.toFixed(2)
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setError('');
    
    if (!formData.saleDate || !formData.platformId || !formData.amount || !formData.orderId) {
      setError('Por favor completa todos los campos obligatorios');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const saleDataForSubmit = {
        saleDate: convertMadridDateTimeLocalToUTCString(formData.saleDate),
        executionDate: formData.executionDate ? convertMadridDateToUTCString(formData.executionDate) : null,
        platformId: parseInt(formData.platformId),
        orderId: formData.orderId,
        amount: parseFloat(formData.amount),
        commission: parseFloat(formData.commission),
        notes: formData.notes,
        status: formData.status,
        customerId: formData.customerId ? parseInt(formData.customerId) : null,
        productId: formData.productId,
        productName: formData.productName,
        campaign: formData.campaign,
        article: formData.article,
        currency: formData.currency,
        customerCountry: formData.customerCountry,
        gclid: formData.gclid
      };

      if (isEditMode) {
        await saleService.updateSale(id, saleDataForSubmit);
        toast.success('Venta actualizada correctamente');
      } else {
        await saleService.createSale(saleDataForSubmit);
        toast.success('Venta creada correctamente');
      }
      
      navigate('/sales');
    } catch (err) {
      console.error('Error al guardar la venta:', err);
      setError('Error al guardar la venta: ' + (err.response?.data?.message || err.message || 'Error desconocido'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="page-title">
          {isEditMode ? 'Editar Venta' : 'Nueva Venta'}
        </h1>
        <div className="page-actions">
          <Link to="/sales" className="btn btn-outline">
            Volver
          </Link>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <div className="card bg-base-100 shadow-xl">
        <form onSubmit={handleSubmit} className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Fecha de Venta</span>
              </label>
              <input
                type="datetime-local"
                name="saleDate"
                className="input input-bordered w-full"
                value={formData.saleDate}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Fecha de Ejecución</span>
              </label>
              <input
                type="date"
                name="executionDate"
                className="input input-bordered w-full"
                value={formData.executionDate}
                onChange={handleChange}
              />
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Plataforma</span>
              </label>
              <select
                name="platformId"
                className="select select-bordered w-full"
                value={formData.platformId}
                onChange={handleChange}
                required
              >
                <option value="">Selecciona una plataforma</option>
                {platforms.map(platform => (
                  <option key={platform.id} value={platform.id}>
                    {platform.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">ID de Orden</span>
              </label>
              <input
                type="text"
                name="orderId"
                className="input input-bordered w-full"
                placeholder="ID de la orden"
                value={formData.orderId}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Producto</span>
              </label>
              <select
                name="productId"
                className="select select-bordered w-full"
                value={formData.productId || ''}
                onChange={(e) => {
                  const selectedProduct = filteredProducts.find(p => p.id === parseInt(e.target.value));
                  setFormData(prev => ({
                    ...prev,
                    productId: e.target.value ? parseInt(e.target.value) : null,
                    productName: selectedProduct ? selectedProduct.name : ''
                  }));
                }}
                disabled={!formData.platformId}
              >
                <option value="">Seleccionar producto</option>
                {filteredProducts.length > 0 ? (
                  filteredProducts.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    {formData.platformId ? 'No hay productos disponibles para esta plataforma' : 'Selecciona primero una plataforma'}
                  </option>
                )}
              </select>
              <div className="flex justify-between mt-2">
                <label className="label-text-alt">
                  {!formData.platformId 
                    ? 'Selecciona primero una plataforma'
                    : filteredProducts.length === 0 
                      ? 'No hay productos disponibles para esta plataforma'
                      : 'Selecciona un producto o crea uno nuevo desde la sección de Productos'}
                </label>
                <Link to="/products" className="text-xs link link-primary" target="_blank">
                  Gestionar productos
                </Link>
              </div>
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Monto</span>
              </label>
              <input
                type="number"
                name="amount"
                className="input input-bordered w-full"
                placeholder="0.00"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Comisión</span>
              </label>
              <input
                type="number"
                name="commission"
                className="input input-bordered w-full"
                placeholder="0.00"
                step="0.01"
                min="0"
                value={formData.commission}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Campaña</span>
              </label>
              <input
                type="text"
                name="campaign"
                className="input input-bordered w-full"
                placeholder="Nombre de la campaña"
                value={formData.campaign}
                onChange={handleChange}
              />
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Artículo</span>
              </label>
              <input
                type="text"
                name="article"
                className="input input-bordered w-full"
                placeholder="Nombre del artículo"
                value={formData.article}
                onChange={handleChange}
              />
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">ID de Cliente</span>
              </label>
              <input
                type="text"
                name="customerId"
                className="input input-bordered w-full"
                placeholder="ID del cliente"
                value={formData.customerId}
                onChange={handleChange}
              />
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">País del Cliente</span>
              </label>
              <select
                name="customerCountry"
                className="select select-bordered w-full"
                value={formData.customerCountry}
                onChange={handleChange}
              >
                <option value="">Sin país</option>
                {countryOptions.map(({ code, name }) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Estado</span>
              </label>
              <select
                name="status"
                className="select select-bordered w-full"
                value={formData.status}
                onChange={handleChange}
                required
              >
                <option value="pending">Pendiente</option>
                <option value="approved">Aprobada</option>
                <option value="rejected">Rechazada</option>
              </select>
            </div>

            {isEditMode && formData.gclid && (
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text font-medium">GCLID</span>
                </label>
                <input
                  type="text"
                  name="gclid"
                  className="input input-bordered w-full bg-base-200"
                  value={formData.gclid}
                  disabled
                />
              </div>
            )}

            <div className="form-control w-full md:col-span-2">
              <label className="label">
                <span className="label-text font-medium">Notas</span>
              </label>
              <textarea
                name="notes"
                className="textarea textarea-bordered w-full h-24"
                placeholder="Notas adicionales"
                value={formData.notes}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="card-actions justify-end mt-6">
            <Link to="/sales" className="btn btn-ghost">
              Cancelar
            </Link>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? (
                <span className="loading loading-spinner"></span>
              ) : isEditMode ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SaleFormScreen; 
